import { ReactNode, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { TabBar } from "./TabBar";
import { Logo } from "./Logo";
import { Bell, ChevronLeft, Settings as SettingsIcon } from "lucide-react";
import { LegalSplash } from "./LegalSplash";
import { PaywallModal } from "./PaywallModal";
import { battleHubReturnPending } from "@/lib/battleHubMemory";
import { ShareToFriendModal } from "./ShareToFriendModal";
import { NexQuestionnaire } from "./NexQuestionnaire";
import { PokemonDetailSheet } from "./PokemonDetailSheet";
import { NotificationsPanel, useInboxUnreadCount } from "./NotificationsPanel";
import { HundoToast } from "./HundoToast";
import { MainMenu } from "@/components/MainMenu";
import { confirmPageExit } from "@/lib/pageExitGuard";
import {
  armPageReturn,
  detailReturnTarget,
  previousPage,
  type PageReturnTarget,
} from "@/lib/pageReturn";
import { useAuth } from "./AuthProvider";

interface AppShellProps {
  title?: string;
  subtitle?: string;
  children: ReactNode;
  rightSlot?: ReactNode;
  pageSpacing?: boolean;
}

// PR-E8 fix 6: cross-component signal for opening the notifications inbox.
// The hero medallion's long-press wifi-arc menu (HeroDexButton) needs to
// trigger the same inbox sheet AppShell already owns. A custom DOM event
// keeps the surface decoupled: AppShell listens, any component dispatches.
export const INBOX_OPEN_EVENT = "nexus:openInbox";

export function AppShell({ title, subtitle, children, rightSlot, pageSpacing }: AppShellProps) {
  const [inboxOpen, setInboxOpen] = useState(false);
  useServerSpriteHydrationOnce();

  /**
   * Publish the header's REAL height as `--app-header-h`.
   *
   * Every pinned filter row on every page has to sit exactly under this header,
   * and the offset used to be a hard-coded 47px derived on paper from
   * `py-2 (16) + 30px logo + 1px border`. Paper is not the device: the PRO pill,
   * a larger dynamic-type setting or a taller right-hand slot all push the real
   * header past 47px, and the rows then cut into it — and, being z-40 against
   * the header's z-30, drew OVER the chrome rather than under it.
   *
   * Measured, so it self-corrects. See lib/stickyOffsets, which reads this with
   * the old 47px as its fallback for the first frame and for any surface that
   * renders without an AppShell.
   */
  const headerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const publish = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      if (h > 0) document.documentElement.style.setProperty("--app-header-h", `${h}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // PR-E8 fix 6: open the inbox from anywhere via a window CustomEvent.
  // HeroDexButton's long-press menu dispatches this when the user picks the
  // "Notifications" arc; AppShell owns the panel state so the sheet sits
  // ABOVE the tab bar regardless of which page is mounted.
  useEffect(() => {
    const onOpen = () => setInboxOpen(true);
    window.addEventListener(INBOX_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(INBOX_OPEN_EVENT, onOpen);
  }, []);

  return (
    <div className="min-h-[100dvh] w-full app-shell-safe-area overflow-x-clip bg-[var(--surface-base)] text-[var(--text-primary)]">
      <div className="app-shell-safe-spacer" aria-hidden />
      
      <div
        className="app-shell-column page-width-guard mx-auto max-w-md md:max-w-2xl lg:max-w-3xl px-4 pb-tabbar"
        // How far a pinned band inside this column has to bleed to reach the
        // content edge. DotRail re-declares it for the extra rail gutter.
        style={{ "--band-bleed": "1rem" } as React.CSSProperties}
      >
        {/* Global Transparent Header */}
        {/* b13f: the header is fully opaque (was bg-…/80 + backdrop-blur-xl).
            On scroll, page content slid under the 80%-translucent blurred bar
            and read as a broken gradient bleeding into the content below.
            An opaque surface keeps the sticky header reading as solid chrome
            in both themes.

            PR-E5-header: the Nexus Dex logo + wordmark is centred in the
            header and DECOUPLED from the variable-width side clusters. The
            logo is absolutely positioned at the header's optical centre
            (left-1/2 + -translate-x-1/2), so it stays anchored regardless of
            whether the left cluster shows a PRO pill or the right cluster
            shows a rightSlot / settings cog / bell. The centre wrapper uses
            `pointer-events-none` with `pointer-events-auto` on the logo so
            the visual centring never blocks taps on the side clusters when
            their hit areas overlap on narrow viewports. */}
        <header
          ref={headerRef}
          className="app-shell-sticky-header relative flex items-center justify-between -mx-4 px-4 py-2 bg-[var(--surface-raised)] border-b border-[var(--border-subtle)]"
        >
          {/* Left cluster: back button + the main menu.
              The tier pill used to live here, spending a permanent slot to tell
              the trainer something they already knew; it is now the first row
              INSIDE the menu, which is where you go to check a status. The menu
              button is narrower than the pill was, so this cluster is smaller
              than before even with the back arrow alongside it, and the centred
              logo has more room rather than less. */}
          <div className="flex items-center gap-1 min-w-0">
            <HeaderBackButton />
            <MainMenu />
          </div>

          {/* Centre overlay: Nexus Dex logo + wordmark, anchored to the
              header's optical centre and decoupled from the side clusters. */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
            <div className="pointer-events-auto">
              <Logo size={30} withWordmark variant="mark" />
            </div>
          </div>

          {/* Right cluster: page rightSlot, inbox bell, then settings cog.
              The bell and cog swapped on 2026-08-14 (user direction). The tour
              never pointed at either — it reaches Settings by ROUTE and frames a
              section inside the page — so the swap could not strand a step. */}
          <div className="flex items-center justify-end gap-1.5">
            {rightSlot}
            <InboxBellButton onOpen={() => setInboxOpen(true)} />
            <HeaderSettingsButton />
          </div>
        </header>

        {/* Dynamic Contextual Title Block */}
        {/* b17m/2026-07-02: pt-4 -> pt-12 so the metallic h1 title AND its
            subtitle clear the sticky header + status-bar safe area on all
            AppShell (V1) pages (the Events "Ranked live events" clip). Matches
            the pt-12 hero spacing the V2 pages already use. The h1 also drops
            overflow-hidden: `.v2-title-metallic` paints via background-clip:text
            and overflow-hidden was shaving the top of the capital glyphs. We
            keep the single-line guarantee with truncate on a min-w-0 wrapper
            (truncate = overflow-hidden+ellipsis on the WRAPPER, not the clipped
            text node) so long titles still ellipsize without cutting glyphs. */}
        {(title || subtitle) && (
          <div className="pt-12 pb-2">
            <div className="min-w-0 max-w-full truncate">
              {title && <h1 className="v2-title-metallic text-2xl font-black tracking-tight whitespace-nowrap leading-[1.15]">{title}</h1>}
            </div>
            {subtitle && <p className="text-sm text-[var(--text-secondary)] mt-1">{subtitle}</p>}
          </div>
        )}

        {/* Mainstage Viewport */}
        <main className={`page-width-guard${pageSpacing ? " page-shell-spacing" : ""}`}>{children}</main>
      </div>

      {/* Global Bottom Navigation System */}
      <TabBar />

      {/* Modals and Overlays */}
      <PaywallModal />
      <LegalSplash />
      {/* GuidedTour is intentionally NOT mounted here. AppShell is rendered by
          every page, so it remounts on each route change — which would reset
          the tour's step index to 0 the moment the tour navigated to another
          tab (profile -> friends snapping back to the welcome step, looping).
          It is mounted ONCE at the app root (App.tsx) instead, next to the
          other navigation-surviving globals, so its state persists across the
          route changes the tour itself drives. */}
      <NexQuestionnaire />
      <PokemonDetailSheet />
      <ShareToFriendModal />
      <NotificationsPanel open={inboxOpen} onOpenChange={setInboxOpen} />
      <HundoToast />
    </div>
  );
}

/**
 * Main-menu routes the bottom TabBar exposes directly. Back button is
 * suppressed on these because there is no "previous page" semantic; the
 * TabBar is the navigation surface.
 */
const MAIN_MENU_ROUTES = new Set(["/", "/friends", "/dex", "/trade", "/calendar"]);

function HeaderBackButton() {
  const [loc, setLocation] = useLocation();
  // WKWebView can leave a page it navigates BACK to unpainted until the
  // first scroll (2026-09-04 device report: the Profile came back blank).
  // A one-pixel scroll nudge after the route commits makes the compositor
  // raster the new tree; on the web it is invisible and harmless.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const id = window.setTimeout(() => {
      const y = window.scrollY;
      window.scrollBy(0, 1);
      window.scrollTo(0, y);
    }, 50);
    return () => window.clearTimeout(id);
  }, [loc]);
  const isMainRoute = MAIN_MENU_ROUTES.has(loc);
  if (isMainRoute) return null;

  const handleBack = () => {
    // The current page may veto the exit (species page Manual Edit with an
    // unresolved regional-link answer; see lib/pageExitGuard). A blocked
    // guard surfaces its own UI, so nothing more to do here.
    if (!confirmPageExit()) return;
    // Back to the page the detail was opened from, at the offset it was left
    // at (pageReturn, 2026-10-12 ask). The arrival restores itself
    // (usePageReturnRestore) once the route reset has stood down.
    const returnTo = (target: PageReturnTarget) => {
      armPageReturn(target);
      setLocation(target.path);
    };
    // Species detail opened FROM THE DEX returns to the Dex grid (2026-08-21
    // ask): after browsing several species in a row, Back should land on the
    // grid, scrolled to the row they left (dexScrollRestore), not unwind
    // detail-by-detail — the iOS edge-swipe still walks the real history for
    // anyone who wants that. Opened from anywhere else (a Profile panel, a
    // Calendar card, a record page), Back is that page.
    if (loc.startsWith("/pokemon/")) {
      const from = detailReturnTarget(loc);
      if (from) returnTo(from);
      else setLocation("/dex");
      return;
    }
    // Same rule for a My Pokémon record (2026-08-31 ask): opened from the
    // list, which the record page can be swiped sideways through, Back
    // returns to the LIST — scrolled to whichever record they ended on
    // (myPokemonScrollRestore) — rather than unwinding record by record.
    // Opened from the Rocket page, a trade, a radar, Back is that page.
    if (loc.startsWith("/my-pokemon/")) {
      // Unless the record was opened from the Battle Hub's compare boxes
      // (2026-09-10 ask): then Back returns to the meta the trainer left,
      // which remembers its mode, filters and offset (battleHubMemory).
      if (battleHubReturnPending()) {
        setLocation("/battle");
        return;
      }
      const from = detailReturnTarget(loc);
      if (from) returnTo(from);
      else setLocation("/my-pokemon");
      return;
    }
    // Everywhere else: the previous page, at the offset it was left at. The
    // real history entry is walked so the stack stays honest; the offset is
    // armed for that page and dropped if the walk lands anywhere else.
    if (typeof window !== "undefined" && window.history.length > 1) {
      const prev = previousPage();
      if (prev) armPageReturn(prev);
      window.history.back();
      return;
    }
    setLocation("/");
  };

  return (
    <button
      type="button"
      data-testid="header-back-button"
      onClick={handleBack}
      aria-label="Back"
      className="w-9 h-9 rounded-full flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-elev-1)] transition-colors"
    >
      <ChevronLeft className="w-[18px] h-[18px]" />
    </button>
  );
}

function useServerSpriteHydrationOnce(): void {
  /*
   * DISABLED 2026-08-13, deliberately left as a no-op rather than deleted.
   *
   * This pulled up to 80 sprite crops down from the server into the on-device
   * store on every sign-in. Nothing displays those crops any more — the art
   * comes from the official mirror everywhere — so it was spending a trainer's
   * network and storage on images with no reader. Worse, it would have refilled
   * the store within seconds of them using the new "Clear stored sprites"
   * button, which is the sort of thing that makes a privacy control look like a
   * lie.
   *
   * The hook stays so the call site and the hydration module keep their shape
   * while the sprite-sync feature is decided on: if crops ever earn a surface
   * again, this is the one line that turns them back on.
   */
}

function HeaderSettingsButton() {
  const [, setLocation] = useLocation();
  return (
    <button
      type="button"
      data-testid="button-header-settings"
      onClick={() => {
        if (!confirmPageExit()) return;
        setLocation("/settings");
      }}
      aria-label="Open settings"
      className="w-9 h-9 rounded-full flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-elev-1)] transition-colors"
    >
      <SettingsIcon className="w-[18px] h-[18px]" />
    </button>
  );
}

function InboxBellButton({ onOpen }: { onOpen: () => void }) {
  const { isSignedIn } = useAuth();
  if (!isSignedIn) {
    return (
      <button
        data-testid="button-notifications"
        type="button"
        disabled
        className="w-9 h-9 rounded-full flex items-center justify-center text-[var(--text-tertiary)] opacity-40"
        aria-label="Notifications"
      >
        <Bell className="w-[18px] h-[18px]" />
      </button>
    );
  }
  return <InboxBellButtonAuthed onOpen={onOpen} />;
}

function InboxBellButtonAuthed({ onOpen }: { onOpen: () => void }) {
  const unread = useInboxUnreadCount();
  return (
    <button
      data-testid="button-notifications"
      type="button"
      onClick={onOpen}
      className="relative w-9 h-9 rounded-full flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
      aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
    >
      <Bell className="w-[18px] h-[18px]" />
      {unread > 0 && (
        <span
          aria-hidden
          data-testid="notifications-unread-dot"
          className="v2-bell-pulse absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[var(--accent-fill)]"
        />
      )}
    </button>
  );
}

/*
 * TierChip lived here until 2026-08-14: a permanent header slot spent telling
 * the trainer a status they already knew. It moved INTO the main menu (see
 * MainMenu's tier row), which is where a status belongs — somewhere you go to
 * check it, and where the same tap can also manage the subscription or open the
 * paywall. Deleted rather than left dark, because a second implementation of
 * "what tier is this" is exactly the thing that drifts.
 */
